import { useTranslation } from 'react-i18next';
import { OnboardingStep, useOnboarding } from '../../../contexts/OnboardingContext';
import OnboardingTooltip from './OnboardingTooltip';
import OnboardingHighlight from './OnboardingHighlight';
import { useNavigate } from 'react-router-dom';

// Map steps to their target element IDs
const STEP_TARGETS: Record<OnboardingStep, string> = {
  [OnboardingStep.WELCOME]: '',
  [OnboardingStep.OVERVIEW_CHART]: 'onboarding-overview-summary',
  [OnboardingStep.SPENDING_DISTRIBUTION]: 'onboarding-spending-chart',
  [OnboardingStep.SPENDING_TRENDS]: 'onboarding-spending-trends',
  [OnboardingStep.ASSET_OVERVIEW]: 'onboarding-asset-overview',
  [OnboardingStep.ASSET_FLUCTUATION]: 'onboarding-asset-chart',
  [OnboardingStep.ADD_TRANSACTION]: 'onboarding-add-transaction',
  [OnboardingStep.COMPLETED]: '',
};

const OnboardingOverlay = () => {
  const { t } = useTranslation(['onboarding']);
  const navigate = useNavigate();
  const {
    isOnboardingActive,
    currentStep,
    nextStep,
    goToStep,
    triggerCompletionModal,
  } = useOnboarding();

  // Don't render if onboarding is not active
  if (!isOnboardingActive || currentStep === OnboardingStep.WELCOME || currentStep === OnboardingStep.COMPLETED) {
    return null;
  }

  // Handle navigation between steps
  const handleOverviewChartNext = () => {
    nextStep();
  };

  const handleSpendingDistributionNext = () => {
    nextStep();
  };

  const handleSpendingTrendsNext = () => {
    // Navigate to assets page for next steps
    goToStep(OnboardingStep.ASSET_OVERVIEW);
    navigate('/assets');
  };

  const handleAssetOverviewNext = () => {
    nextStep();
  };

  const handleAssetFluctuationNext = () => {
    // Navigate back to overview for final step
    goToStep(OnboardingStep.ADD_TRANSACTION);
    navigate('/overview');
  };

  const handleAddTransactionNext = () => {
    triggerCompletionModal();
  };

  const handleAddNow = () => {
    triggerCompletionModal();
    navigate('/transactions/create');
  };

  // Get tooltip config for current step
  const getTooltipConfig = () => {
    switch (currentStep) {
      case OnboardingStep.OVERVIEW_CHART:
        return {
          title: t('step_overview_chart_title'),
          description: t('step_overview_chart_description'),
          position: 'bottom' as const,
          onNext: handleOverviewChartNext,
        };

      case OnboardingStep.SPENDING_DISTRIBUTION:
        return {
          title: t('step_spending_distribution_title'),
          description: t('step_spending_distribution_description'),
          position: 'top' as const,
          onNext: handleSpendingDistributionNext,
        };

      case OnboardingStep.SPENDING_TRENDS:
        return {
          title: t('step_spending_trends_title'),
          description: t('step_spending_trends_description'),
          position: 'bottom' as const,
          onNext: handleSpendingTrendsNext,
        };

      case OnboardingStep.ASSET_OVERVIEW:
        return {
          title: t('step_asset_overview_title'),
          description: t('step_asset_overview_description'),
          position: 'bottom' as const,
          onNext: handleAssetOverviewNext,
        };

      case OnboardingStep.ASSET_FLUCTUATION:
        return {
          title: t('step_asset_fluctuation_title'),
          description: t('step_asset_fluctuation_description'),
          position: 'bottom' as const,
          onNext: handleAssetFluctuationNext,
        };

      case OnboardingStep.ADD_TRANSACTION:
        return {
          title: t('step_add_transaction_title'),
          description: t('step_add_transaction_description'),
          position: 'top' as const,
          onNext: handleAddTransactionNext,
          showAddNow: true,
          onAddNow: handleAddNow,
        };

      default:
        return null;
    }
  };

  const tooltipConfig = getTooltipConfig();
  const targetId = STEP_TARGETS[currentStep];

  if (!tooltipConfig) return null;

  // Get highlight config based on step
  // Use borderRadius: 16 to match Paper component (borderRadius: 2 in MUI = 16px)
  const getHighlightConfig = () => {
    switch (currentStep) {
      case OnboardingStep.SPENDING_DISTRIBUTION:
        return { paddingX: 8, paddingY: 8, borderRadius: 16 };
      case OnboardingStep.SPENDING_TRENDS:
        return { paddingX: 0, paddingTop: 8, paddingBottom: 0, borderRadius: 16 };
      case OnboardingStep.ADD_TRANSACTION:
        return { paddingTop: 30, paddingBottom: 0, paddingX: 0, borderRadius: 0, shape: 'tShape' as const, buttonWidth: 70, buttonHeight: 30 };
      default:
        return { padding: 0, borderRadius: 16 };
    }
  };

  const highlightConfig = getHighlightConfig();

  return (
    <>
      {/* Highlight with spotlight effect */}
      {targetId && (
        <OnboardingHighlight
          targetId={targetId}
          {...highlightConfig}
        />
      )}

      {/* Tooltip */}
      <OnboardingTooltip
        step={currentStep}
        targetId={targetId}
        title={tooltipConfig.title}
        description={tooltipConfig.description}
        position={tooltipConfig.position}
        onNext={tooltipConfig.onNext}
        showExit={(tooltipConfig as any).showExit}
        showAddNow={(tooltipConfig as any).showAddNow}
        onAddNow={(tooltipConfig as any).onAddNow}
      />
    </>
  );
};

export default OnboardingOverlay;
